import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Sparkles, AlertTriangle, Lightbulb, Award, Eye, ShoppingBag } from "lucide-react";
import { api } from "../../lib/api";

const KIND_ICON = {
  positive: { Icon: Award, color: "text-kult-yellow", border: "border-kult-yellow" },
  alert: { Icon: AlertTriangle, color: "text-kult-pink", border: "border-kult-pink" },
  warning: { Icon: AlertTriangle, color: "text-orange-400", border: "border-orange-400" },
  info: { Icon: Lightbulb, color: "text-blue-300", border: "border-blue-300" },
};

export function AdminInsightsTab() {
  const [overview, setOverview] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiStrategy, setAiStrategy] = useState("");
  const [aiError, setAiError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [o, t, tr, s] = await Promise.all([
          api.get("/admin/insights/overview"),
          api.get("/admin/insights/top-products"),
          api.get("/admin/insights/traffic"),
          api.get("/admin/insights/suggestions"),
        ]);
        if (!alive) return;
        setOverview(o.data);
        setTopProducts(t.data);
        setTraffic(tr.data);
        setSuggestions(s.data.suggestions || []);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Could not load insights");
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function generateAiStrategy() {
    setAiBusy(true);
    setAiError("");
    setAiStrategy("");
    try {
      const { data } = await api.post("/admin/insights/ai-strategy");
      setAiStrategy(data.strategy || "");
    } catch (e) {
      setAiError(e?.response?.data?.detail || "AI strategy unavailable. Try again later.");
    } finally {
      setAiBusy(false);
    }
  }

  if (loading) {
    return <p className="font-mono text-xs text-kult-muted py-12" data-testid="insights-loading">Loading insights…</p>;
  }
  if (error) {
    return <p className="font-mono text-xs text-kult-pink py-12" data-testid="insights-error">{error}</p>;
  }

  return (
    <section data-testid="admin-insights-section" className="space-y-12">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-1">/ INSIGHTS</p>
          <h2 className="font-display text-4xl md:text-5xl">LAST 30 DAYS</h2>
          <p className="font-mono text-[11px] text-kult-muted mt-2">Self-hosted, cookieless. No third-party trackers.</p>
        </div>
        <button
          type="button"
          onClick={generateAiStrategy}
          disabled={aiBusy}
          className="inline-flex items-center gap-2 px-4 py-3 bg-kult-pink text-black font-mono text-xs tracking-[0.25em] hover:bg-white disabled:opacity-50"
          data-testid="insights-ai-button"
        >
          <Sparkles size={14} />
          {aiBusy ? "GENERATING…" : "GET AI STRATEGY"}
        </button>
      </header>

      {/* AI strategy */}
      {(aiStrategy || aiError) && (
        <div className="border-2 border-kult-pink p-6 bg-kult-black2" data-testid="insights-ai-output">
          <p className="font-mono text-[10px] tracking-[0.3em] text-kult-pink mb-3">/ AI STRATEGY</p>
          {aiError ? (
            <p className="font-mono text-sm text-kult-pink">{aiError}</p>
          ) : (
            <div className="prose prose-invert max-w-none font-body text-base leading-relaxed whitespace-pre-wrap">
              {aiStrategy}
            </div>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="insights-kpis">
        <KpiCard label="REVENUE" value={`€${(overview.revenue.d30 || 0).toFixed(2)}`} delta={overview.revenue.delta_pct} testId="kpi-revenue" />
        <KpiCard label="ORDERS" value={overview.orders.d30} delta={overview.orders.delta_pct} testId="kpi-orders" />
        <KpiCard label="SESSIONS" value={overview.sessions.d30} delta={overview.sessions.delta_pct} testId="kpi-sessions" />
        <KpiCard label="CONVERSION" value={`${overview.conversion_pct}%`} sub={`${overview.pageviews.d30} pageviews`} testId="kpi-conv" />
      </div>

      {/* Suggestions */}
      <section>
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">/ SUGGESTIONS</p>
        <div className="space-y-3" data-testid="insights-suggestions">
          {suggestions.map((s, i) => {
            const meta = KIND_ICON[s.kind] || KIND_ICON.info;
            const Icon = meta.Icon;
            return (
              <div key={i} className={`border-l-4 ${meta.border} bg-kult-black2 p-4 flex gap-3`} data-testid={`insight-suggestion-${i}`}>
                <Icon size={18} className={`${meta.color} mt-0.5 shrink-0`} />
                <div>
                  <p className="font-display text-xl leading-tight">{s.title}</p>
                  <p className="text-kult-muted text-sm mt-1">{s.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Top sellers + top viewed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProductTable title="TOP BY SALES" Icon={ShoppingBag} rows={topProducts.top_by_sales} mode="sales" testId="top-by-sales" />
        <ProductTable title="TOP BY VIEWS" Icon={Eye} rows={topProducts.top_by_views} mode="views" testId="top-by-views" />
      </div>

      {/* Underperformers + cold */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProductTable title="UNDERPERFORMERS (HIGH VIEWS, LOW SALES)" Icon={AlertTriangle} rows={topProducts.underperformers} mode="conv" emptyMsg="None — every viewed product is selling." testId="underperformers" />
        <ColdProductsTable rows={topProducts.cold} />
      </div>

      {/* Traffic — top paths and referrers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="border border-kult-border p-5" data-testid="top-paths">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3">/ TOP PAGES</p>
          {traffic.top_paths.length === 0 ? (
            <p className="text-kult-muted font-mono text-sm">No pageview data yet.</p>
          ) : (
            <ul className="space-y-2">
              {traffic.top_paths.map((p) => (
                <li key={p.path} className="flex justify-between gap-2 border-b border-kult-border pb-2 last:border-b-0">
                  <span className="font-mono text-xs text-white truncate" title={p.path}>{p.path}</span>
                  <span className="font-mono text-xs text-kult-muted shrink-0">{p.views} views · {p.unique_sessions} sessions</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="border border-kult-border p-5" data-testid="top-referrers">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3">/ REFERRERS</p>
          {traffic.top_referrers.length === 0 ? (
            <p className="text-kult-muted font-mono text-sm">Direct traffic only — share the link on socials and you'll see sources here.</p>
          ) : (
            <ul className="space-y-2">
              {traffic.top_referrers.map((r) => (
                <li key={r.host} className="flex justify-between gap-2 border-b border-kult-border pb-2 last:border-b-0">
                  <span className="font-mono text-xs text-white truncate" title={r.host}>{r.host}</span>
                  <span className="font-mono text-xs text-kult-muted shrink-0">{r.visits} visits</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </section>
  );
}

function KpiCard({ label, value, delta, sub, testId }) {
  const showDelta = delta !== undefined && delta !== null;
  const positive = showDelta && delta > 0;
  return (
    <div className="border border-kult-border p-4" data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">{label}</p>
      <p className="font-display text-3xl mt-1">{value}</p>
      {showDelta && (
        <p className={`font-mono text-[11px] mt-1 flex items-center gap-1 ${positive ? "text-kult-yellow" : "text-kult-pink"}`}>
          {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {positive ? "+" : ""}{delta}% vs prev 30d
        </p>
      )}
      {sub && <p className="font-mono text-[10px] text-kult-muted mt-1">{sub}</p>}
    </div>
  );
}

function ProductTable({ title, Icon, rows, mode, emptyMsg, testId }) {
  return (
    <section className="border border-kult-border p-5" data-testid={testId}>
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3 flex items-center gap-2">
        <Icon size={14} /> {title}
      </p>
      {(!rows || rows.length === 0) ? (
        <p className="text-kult-muted font-mono text-sm">{emptyMsg || "Not enough data yet."}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.product_id} className="flex items-center gap-3 border-b border-kult-border pb-3 last:border-b-0">
              {r.image && <img src={r.image} alt="" className="w-12 h-12 object-cover bg-black border border-kult-border" />}
              <div className="flex-1 min-w-0">
                <p className="font-display text-base leading-tight truncate">{r.name}</p>
                <p className="font-mono text-[10px] text-kult-muted">€{(r.price || 0).toFixed(2)}</p>
              </div>
              <div className="text-right font-mono text-[11px]">
                {mode === "sales" && (
                  <>
                    <p className="text-kult-yellow">{r.units_sold} sold</p>
                    <p className="text-kult-muted">€{r.revenue.toFixed(2)}</p>
                  </>
                )}
                {mode === "views" && (
                  <>
                    <p className="text-kult-yellow">{r.views} views</p>
                    <p className="text-kult-muted">{r.units_sold} sold</p>
                  </>
                )}
                {mode === "conv" && (
                  <>
                    <p className="text-kult-pink">{r.views} v / {r.units_sold} s</p>
                    <p className="text-kult-muted">{r.conversion_pct}% conv</p>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ColdProductsTable({ rows }) {
  return (
    <section className="border border-kult-border p-5" data-testid="cold-products">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3 flex items-center gap-2">
        <AlertTriangle size={14} /> COLD PRODUCTS (NO VIEWS / NO SALES)
      </p>
      {rows.length === 0 ? (
        <p className="text-kult-muted font-mono text-sm">All products are getting eyeballs. Nice.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.product_id} className="flex items-center gap-3 border-b border-kult-border pb-3 last:border-b-0">
              {r.image && <img src={r.image} alt="" className="w-12 h-12 object-cover bg-black border border-kult-border" />}
              <div className="flex-1 min-w-0">
                <p className="font-display text-base leading-tight truncate">{r.name}</p>
                <p className="font-mono text-[10px] text-kult-muted">€{(r.price || 0).toFixed(2)} · /shop/{r.slug}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
